// frontend/app/components/lessonPlans/useVoiceAnswer.js
//
// Speak an answer instead of typing it.
//
// The Saras Learning Loop asks students to *explain* — "Explain to your
// classmate how this works" — and a 14-year-old will say more out loud in
// thirty seconds than they will type in three minutes. The Explain phase is
// also the heaviest evidence in the mastery model, so the cheapest way to get
// better evidence is to lower the cost of producing it.
//
// Typing is never taken away. This is an additional way in: where there is no
// microphone, no permission, or no MediaRecorder (native builds, locked-down
// browsers, a denied prompt), `supported` is false, nothing renders, and the
// text field is exactly what it was.
//
// Reuses the transcription route the ask-bar already uses
// (`POST /api/whisper-transcribe/`, FormData `file` + `language`, returns
// `{ transcription }`) rather than introducing a second speech path.

import { useCallback, useEffect, useRef, useState } from "react";

import { FASTAPI_BASE_URL } from "../../config";

/** Long enough for a considered explanation, short enough to bound an upload. */
export const MAX_RECORDING_MS = 90000;

/** Whisper takes webm/opus natively; the rest are fallbacks for other engines. */
const preferredMimeType = () => {
  if (typeof MediaRecorder === "undefined") return undefined;
  if (MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus")) {
    return "audio/webm;codecs=opus";
  }
  if (MediaRecorder.isTypeSupported?.("audio/webm")) return "audio/webm";
  return undefined;
};

const extensionFor = (mime) => {
  if (!mime) return "webm";
  if (mime.includes("webm")) return "webm";
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("mp4")) return "mp4";
  return "wav";
};

/**
 * Is speaking an answer possible at all in this runtime?
 *
 * Checked once, at module level semantics — a native build has no
 * `navigator.mediaDevices` and no `MediaRecorder`, and must fall straight
 * through to typing without rendering a button that cannot work.
 */
export const isVoiceAnswerSupported = () => {
  try {
    return (
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== "undefined"
    );
  } catch {
    return false;
  }
};

/**
 * @param {object}   options
 * @param {function} options.onTranscript  (text) => void — called once, with
 *   the transcript. The caller decides whether to append or replace; this hook
 *   never touches the answer itself.
 * @param {string}   options.language      language code sent to the transcriber
 * @param {function} options.onEvent       optional; fires exploration events so
 *   TeachSense can see that an answer was spoken rather than typed
 */
export default function useVoiceAnswer({
  onTranscript,
  language = "en",
  onEvent,
} = {}) {
  const [supported] = useState(isVoiceAnswerSupported);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState(null);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  // Callbacks change identity on every render of the calling component; a ref
  // keeps stop/start stable so an in-flight recording is never orphaned.
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const releaseStream = useCallback(() => {
    try {
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
    } catch {
      /* the tracks are already gone; nothing to release */
    }
    streamRef.current = null;
    recorderRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Releasing the microphone is not optional. A component unmounted mid-record
  // (the student changes step while speaking) would otherwise leave the browser
  // tab's recording indicator on indefinitely.
  useEffect(() => () => releaseStream(), [releaseStream]);

  const transcribe = useCallback(
    async (file) => {
      setTranscribing(true);
      try {
        const form = new FormData();
        form.append("file", file);
        form.append("language", language || "en");

        const response = await fetch(`${FASTAPI_BASE_URL}/whisper-transcribe/`, {
          method: "POST",
          body: form,
        });
        const data = await response.json();

        const text = (data?.transcription || "").trim();
        if (!text) {
          // A silent or unintelligible recording is not an error the student
          // caused; say what to do rather than what went wrong.
          setError("I could not make that out. Try again, or type your answer.");
          return;
        }
        setError(null);
        onTranscriptRef.current?.(text);
        onEventRef.current?.({
          event_type: "voice_answer",
          payload: { characters: text.length },
        });
      } catch {
        setError("Could not turn that into text. Your answer can be typed instead.");
      } finally {
        setTranscribing(false);
      }
    },
    [language]
  );

  const stop = useCallback(() => {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch {
      releaseStream();
      setRecording(false);
    }
  }, [releaseStream]);

  /** Abandon the recording without transcribing it. */
  const cancel = useCallback(() => {
    cancelledRef.current = true;
    stop();
  }, [stop]);

  const start = useCallback(async () => {
    if (!supported || recording || transcribing) return;
    setError(null);
    cancelledRef.current = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = preferredMimeType();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      recorderRef.current = recorder;

      const chunks = [];
      recorder.ondataavailable = (event) => {
        if (event?.data?.size) chunks.push(event.data);
      };

      recorder.onstop = async () => {
        const wasCancelled = cancelledRef.current;
        const actualMime = recorder.mimeType || "audio/webm";
        releaseStream();
        setRecording(false);

        if (wasCancelled || chunks.length === 0) return;

        // Packaging the recording can fail on its own (an exotic chunk type, a
        // runtime without File). Without this guard the rejection is swallowed
        // by the async handler and the student is left looking at a control
        // that did nothing at all — the one outcome worse than an error.
        let file;
        try {
          const blob = new Blob(chunks, { type: actualMime });
          file = new File([blob], `answer.${extensionFor(actualMime)}`, {
            type: actualMime,
          });
        } catch {
          setError("That recording could not be saved — type your answer instead.");
          return;
        }
        await transcribe(file);
      };

      recorder.start();
      setRecording(true);
      onEventRef.current?.({ event_type: "voice_answer", payload: { started: true } });

      timerRef.current = setTimeout(stop, MAX_RECORDING_MS);
    } catch (caught) {
      releaseStream();
      setRecording(false);
      // A denied prompt is a choice, not a fault. Say what still works.
      setError(
        caught?.name === "NotAllowedError"
          ? "No microphone access — type your answer instead."
          : "The microphone is unavailable — type your answer instead."
      );
    }
  }, [supported, recording, transcribing, releaseStream, stop, transcribe]);

  return { supported, recording, transcribing, error, start, stop, cancel };
}
